import { useEffect } from 'react';
import { useReportMeasurement } from '@/context/ReportMeasurementContext';
import { resolveClassificationColor } from '@/lib/classification-color';
import { resolveTableCellColor } from '@/lib/table-cell-color';
import { getHeaderRowCount, getHeaderRows } from '@/lib/table-utils';
import { Alignments } from '@/types/report';
import { TableBlock } from '@/types/report-payload';
import { getTableHeadMeasurementId, getTableRowMeasurementId } from '../pagination/utils';
import { TableCell } from './TableCell';
import {
    CellPosition,
    SelectionRange,
    TableSection,
    getSelectionRectGlobal,
    isCellInSelection,
    toGlobalRow,
} from './TableSelection';
import type { MouseEvent, RefObject } from 'react';

interface TableRendererProps {
    block: TableBlock;
    selection: SelectionRange | null;
    editingCell: CellPosition | null;
    disabled: boolean;
    headerColor: string;
    headerTextColor: string;
    textAlignment: Alignments;
    /**
     * Coluna cujas células recebem cor a partir do rótulo de classificação, ou
     * -1 quando a tabela não tem essa coluna ou o laudo desligou o ajuste. Já
     * chega resolvido para o renderer não precisar conhecer a customização.
     */
    classificationColIndex: number;
    onCellMouseDown: (position: CellPosition, event: MouseEvent) => void;
    onCellMouseEnter: (position: CellPosition, event: MouseEvent) => void;
    onCellClick: (position: CellPosition, event: MouseEvent) => void;
    onCellContextMenu: (position: CellPosition, event: MouseEvent) => void;
    onStartEdit: (position: CellPosition, event?: MouseEvent) => void;
    onCommit: (position: CellPosition, value: string, reason?: 'enter' | 'blur') => void;
    onCancelEdit: () => void;
    onDraftChange?: (value: string) => void;
    onOpenCalculator: (rowIndex: number, colIndex: number) => void;
    isCalculatableColumn: (colIndex: number) => boolean;
    onResizeStart: (colIndex: number, event: MouseEvent) => void;
    onScroll?: () => void;
    editSeedByCellKey: Map<string, { key: number; value: string }>;
    editCursorPosition: number | null;
    scrollContainerRef: RefObject<HTMLDivElement>;
    tableInnerRef: RefObject<HTMLDivElement>;
    tableRef: RefObject<HTMLTableElement>;
    measurementId?: string;
}

export function TableRenderer({
    block,
    selection,
    editingCell,
    disabled,
    headerColor,
    headerTextColor,
    textAlignment,
    classificationColIndex,
    onCellMouseDown,
    onCellMouseEnter,
    onCellClick,
    onCellContextMenu,
    onStartEdit,
    onCommit,
    onCancelEdit,
    onDraftChange,
    onOpenCalculator,
    isCalculatableColumn,
    onResizeStart,
    onScroll,
    editSeedByCellKey,
    editCursorPosition,
    scrollContainerRef,
    tableInnerRef,
    tableRef,
    measurementId,
}: TableRendererProps) {
    const { registerHeight } = useReportMeasurement();
    const headerRows = getHeaderRows(block);
    const headerRowCount = getHeaderRowCount(block);
    const selectionRect = selection ? getSelectionRectGlobal(selection, headerRowCount) : null;

    useEffect(() => {
        if (!measurementId || !tableRef.current) return;

        const table = tableRef.current;
        const getReportZoom = () => {
            const zoomContainer = table.closest('[data-report-zoom]') as HTMLElement | null;
            const rawZoom = Number.parseFloat(
                zoomContainer?.getAttribute('data-report-zoom') || '1',
            );

            return Number.isFinite(rawZoom) && rawZoom > 0 ? rawZoom : 1;
        };

        const measureRows = () => {
            const zoom = getReportZoom();
            // O `<thead>` inteiro, não a primeira linha: com uma faixa de grupo
            // acima da folha, medir só a linha 0 subestimaria o cabeçalho.
            const tableHead = table.tHead;

            if (tableHead) {
                registerHeight(
                    getTableHeadMeasurementId(measurementId),
                    tableHead.getBoundingClientRect().height / zoom,
                );
            }

            Array.from(table.tBodies)
                .flatMap((body) => Array.from(body.rows))
                .forEach((row, index) => {
                    const rowId = block.rows[index]?.id;
                    if (!rowId) return;

                    registerHeight(
                        getTableRowMeasurementId(measurementId, rowId),
                        row.getBoundingClientRect().height / zoom,
                    );
                });
        };

        const observer = new ResizeObserver(() => {
            measureRows();
        });

        observer.observe(table);
        Array.from(table.querySelectorAll('tr')).forEach((row) => {
            observer.observe(row);
        });

        measureRows();

        return () => observer.disconnect();
    }, [block.rows, measurementId, registerHeight, tableRef]);

    const renderRow = (
        section: TableSection,
        rowIndex: number,
        cells: TableBlock['header']['cells'],
        isHeader: boolean,
    ) => {
        // Só a folha carrega a alça de redimensionar e o `data-col-index`: numa
        // faixa o colIndex é o da primeira coluna do grupo, e arrastar ali
        // mexeria na coluna errada.
        const isLeafHeader = isHeader && rowIndex === headerRowCount - 1;

        return cells.map((cell, colIndex) => {
            if (cell.mergedInto) return null;

            const position: CellPosition = { section, rowIndex, colIndex };
            const isSelected = isCellInSelection(position, selection, headerRowCount);
            const rowSpan = cell.rowSpan || 1;
            const colSpan = cell.colSpan || 1;
            const globalRow = toGlobalRow(position, headerRowCount);
            const selectionEdges =
                isSelected && selectionRect
                    ? {
                          top: globalRow === selectionRect.rowStart,
                          right: colIndex + colSpan - 1 === selectionRect.colEnd,
                          bottom: globalRow + rowSpan - 1 === selectionRect.rowEnd,
                          left: colIndex === selectionRect.colStart,
                      }
                    : undefined;
            const isEditing =
                !!editingCell &&
                editingCell.section === section &&
                editingCell.rowIndex === rowIndex &&
                editingCell.colIndex === colIndex;
            const seedEntry = editSeedByCellKey.get(cell.id);
            // Precedence: the cell's own color, then the one derived from the
            // classification label, then the header's (a laudo customization).
            // A hand-picked color wins because it is the escape hatch for a
            // symptom scale, where high is worse and the dictionary refuses to
            // guess the direction.
            const cellColor =
                resolveTableCellColor(cell.color) ??
                (!isHeader && colIndex === classificationColIndex
                    ? resolveTableCellColor(resolveClassificationColor(cell.value))
                    : undefined);

            return (
                <TableCell
                    key={cell.id}
                    cell={cell}
                    isHeader={isHeader}
                    isSelected={isSelected}
                    selectionEdges={selectionEdges}
                    isEditing={isEditing}
                    disabled={disabled}
                    textAlignment={textAlignment}
                    textColor={cellColor || (isHeader ? headerTextColor : undefined)}
                    backgroundColor={isHeader ? headerColor : undefined}
                    onMouseDown={(event) => onCellMouseDown(position, event)}
                    onMouseEnter={(event) => onCellMouseEnter(position, event)}
                    onClick={(event) => onCellClick(position, event)}
                    onContextMenu={(event) => onCellContextMenu(position, event)}
                    onStartEdit={(event) => onStartEdit(position, event)}
                    onCommit={(value, reason) => onCommit(position, value, reason)}
                    onCancel={onCancelEdit}
                    editSeedValue={seedEntry?.value}
                    editSeedKey={seedEntry?.key}
                    editCursorPosition={isEditing ? editCursorPosition : null}
                    onOpenCalculator={
                        !isHeader && isCalculatableColumn(colIndex)
                            ? () => onOpenCalculator(rowIndex, colIndex)
                            : undefined
                    }
                    isLeafHeader={isLeafHeader}
                    onResizeStart={
                        isLeafHeader ? (event) => onResizeStart(colIndex, event) : undefined
                    }
                    onDraftChange={isEditing ? onDraftChange : undefined}
                    colIndex={colIndex}
                />
            );
        });
    };

    return (
        <div ref={scrollContainerRef} className="overflow-auto" onScroll={onScroll}>
            {/* `align-bottom` kills the inline-block descender gap — a few px of
                blank line-box under every table. */}
            <div ref={tableInnerRef} className="relative inline-block min-w-full align-bottom">
                <table
                    ref={tableRef}
                    className="w-full table-fixed border-collapse select-none text-xs"
                >
                    <colgroup>
                        {block.columnWidths.map((width, index) => (
                            <col key={`col-${index}`} style={{ width }} />
                        ))}
                    </colgroup>
                    <thead>
                        {headerRows.map((row, rowIndex) => (
                            <tr key={row.id}>{renderRow('header', rowIndex, row.cells, true)}</tr>
                        ))}
                    </thead>
                    <tbody>
                        {block.rows.map((row, rowIndex) => (
                            <tr
                                key={row.id}
                                data-row-index={rowIndex}
                                className={rowIndex % 2 === 0 ? 'bg-white' : 'bg-slate-50'}
                            >
                                {renderRow('body', rowIndex, row.cells, false)}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
